/**
 * Local onboarding progress (spec §9.4 "kein Fortschrittsverlust bei
 * Unterbrechung", §11.1 "Ein technischer Fehler darf niemals die Position im
 * Onboarding verlieren").
 *
 * Two things live here, both keyed per knowledge-source scope:
 *
 *   1. `completed` — the steps the user reported as done by hand. Steps whose
 *      completion the backend can prove (mission mastery, deck review state)
 *      are NOT stored here; they are recomputed from the server every load, so
 *      progress can never drift away from the evidence.
 *   2. `plan` — the generated path itself. Re-planning costs an LLM call and
 *      would produce different step ids, which would orphan every completion.
 *      So the plan is written once and reused until the user regenerates.
 *
 * Storage key follows the `etch.` contract (spec §14). The pre-Etch
 * `onboarding-progress:*` key is deliberately abandoned, not migrated.
 */

export const PROGRESS_STORAGE_KEY = 'etch.onboarding.progress';

/** The scope a plan and its completions belong to. */
export function progressScope(workspacePath) {
  return workspacePath || 'default';
}

function safeStorage() {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage;
  } catch {
    // Safari private mode / storage disabled. The screen still works; it just
    // cannot remember anything across a reload. Never throw into render.
    return null;
  }
}

function readAll() {
  const store = safeStorage();
  if (!store) return {};
  try {
    const raw = store.getItem(PROGRESS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  const store = safeStorage();
  if (!store) return false;
  try {
    store.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(all));
    return true;
  } catch {
    // Quota exceeded — the user keeps their in-memory progress for this visit.
    return false;
  }
}

const EMPTY = Object.freeze({ completed: {}, plan: null, planCreatedAt: null, lastPathId: null, lastStepId: null });

/** Everything remembered for one scope. Always returns a usable object. */
export function readProgress(workspacePath) {
  const entry = readAll()[progressScope(workspacePath)];
  if (!entry || typeof entry !== 'object') return { ...EMPTY };
  return {
    completed: entry.completed && typeof entry.completed === 'object' ? entry.completed : {},
    plan: Array.isArray(entry.plan) ? entry.plan : null,
    planCreatedAt: entry.planCreatedAt || null,
    lastPathId: entry.lastPathId || null,
    lastStepId: entry.lastStepId || null,
  };
}

/** Merge a partial update into one scope. Returns the merged entry. */
export function writeProgress(workspacePath, patch) {
  const scope = progressScope(workspacePath);
  const all = readAll();
  const next = { ...readProgress(workspacePath), ...patch };
  all[scope] = next;
  writeAll(all);
  return next;
}

/** Toggle one self-reported step. Returns the new `completed` map. */
export function toggleCompletion(workspacePath, stepId, done) {
  const current = readProgress(workspacePath);
  const completed = { ...current.completed };
  if (done) completed[stepId] = new Date().toISOString();
  else delete completed[stepId];
  writeProgress(workspacePath, { completed, lastStepId: stepId });
  return completed;
}
